import { GridApi } from "@ag-grid-community/core";
import { useToast } from "@chakra-ui/react";
import {
  TcmEncounter,
  UpdateTcmEncounterResponse,
} from "@metriport/shared-internal/dist/api/tcm-encounter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  putTcmDischargeSummaryPath,
  putTcmEncounterNote,
  putTcmOutreachLog,
  putTcmOutreachStatus,
} from "./api";

export type OutreachStatus = "Not Started" | "Attempted" | "Completed";

export function useTcmMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const updateSummaryPathMutation = useMutation({
    mutationFn: ({ encounterId, title }: { encounterId: string; title: string }) =>
      putTcmDischargeSummaryPath(encounterId, title),
    onSuccess: (_, { encounterId, title }) => {
      const queryKey = ["tcm", "encounter"];
      queryClient.setQueryData(queryKey, (old: TcmEncounter[] | undefined) =>
        old?.map(e => (e.id === encounterId ? { ...e, dischargeSummaryPath: title } : e))
      );
      toast({ title: "Summary path saved", status: "success", duration: 3000, isClosable: true });
    },
    onError: err => {
      console.error("Failed to update summary path:", err);
      toast({
        title: "Error updating summary path",
        description: "Failed to save. Please try again.",
        status: "error",
      });
    },
  });

  const updateOutreachStatusMutation = useMutation({
    mutationFn: ({
      encounterId,
      outreachStatus,
    }: {
      encounterId: string;
      outreachStatus: OutreachStatus;
      gridApi: GridApi;
    }) => putTcmOutreachStatus(encounterId, outreachStatus),
    onSuccess: (result, { encounterId, gridApi }) => {
      const queryKey = ["tcm", "encounter", encounterId];
      queryClient.setQueryData(queryKey, (old: TcmEncounter[] | undefined) =>
        old?.map(e =>
          e.id === encounterId
            ? {
                ...e,
                outreachStatus: result.outreachStatus,
                lastOutreachDate: result.lastOutreachDate,
              }
            : e
        )
      );
      gridApi.getRowNode(encounterId)?.setDataValue("outreachStatus", result.outreachStatus);
      gridApi.getRowNode(encounterId)?.setDataValue("lastOutreachDate", result.lastOutreachDate);
    },
    onError: err => {
      console.error("Failed to update outreach status:", err);
      toast({
        title: "Error saving outreach status",
        description: "Please try again.",
        status: "error",
      });
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: ({ encounterId, note }: { encounterId: string; note: string }) =>
      updateTcmEncounterNote(encounterId, note),
    onSuccess: (_, variables) => {
      const queryKey = ["tcm", "encounter"];

      queryClient.setQueryData(queryKey, (oldData: TcmEncounter[] | undefined) => {
        if (!oldData) return oldData;

        return oldData.map(encounter =>
          encounter.id === variables.encounterId
            ? { ...encounter, freetextNote: variables.note }
            : encounter
        );
      });

      toast({
        title: "Note saved",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: error => {
      console.error("Failed to update note:", error);
      toast({
        title: "Error updating note",
        description: "Failed to save the note. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const updateOutreachLogMutation = useMutation({
    mutationFn: ({ encounterId, encounter }: { encounterId: string; encounter: TcmEncounter }) => {
      return putTcmOutreachLog(encounterId, encounter.outreachLogs || [], encounter.outreachStatus);
    },
    onSuccess: (result: UpdateTcmEncounterResponse, { encounterId }) => {
      const queryKey = ["tcm", "encounter"];
      queryClient.setQueryData(queryKey, (old: TcmEncounter[] | undefined) =>
        old?.map(e =>
          e.id === encounterId
            ? {
                ...e,
                outreachStatus: result.outreachStatus ?? e.outreachStatus,
                lastOutreachDate: result.lastOutreachDate ?? e.lastOutreachDate,
                outreachLogs: result.outreachLogs ?? e.outreachLogs,
              }
            : e
        )
      );
      toast({ title: "Outreach log updated", status: "success", duration: 3000, isClosable: true });
    },
    onError: () => {
      toast({ title: "Error updating outreach log", status: "error" });
    },
  });

  const onSummaryFilePathChange = useCallback(
    (encounterId: string, title: string) => {
      updateSummaryPathMutation.mutate({ encounterId, title });
    },
    [updateSummaryPathMutation]
  );

  const handleOutreachUpdate = useCallback(
    (encounterId: string, outreachStatus: OutreachStatus, gridApi: GridApi) => {
      updateOutreachStatusMutation.mutate({ encounterId, outreachStatus, gridApi });
    },
    [updateOutreachStatusMutation]
  );

  const updateTcmEncounterNote = useCallback(
    async (encounterId: string, note: string): Promise<UpdateTcmEncounterResponse> => {
      return putTcmEncounterNote(encounterId, note);
    },
    []
  );

  return {
    onSummaryFilePathChange,
    handleOutreachUpdate,
    updateSummaryPathMutation,
    updateOutreachStatusMutation,
    updateNoteMutation,
    updateOutreachLogMutation,
  };
}
